import {createContext, Dispatch, PropsWithChildren, useCallback, useContext, useEffect, useRef, useState} from "react";
import {Indicator, IndicatorResponse, IndicatorResponseUploadedFile, SurveySession, UploadedFile} from "@/types/models";
import {SurveySessionInertiaProps} from "@/app";
import {PageProps} from "@/types";
import {Meta, Uppy, UppyFile} from "@uppy/core";
import ThumbnailGenerator from '@uppy/thumbnail-generator';
import {toast} from "react-toastify";
import {useSurveySessionContext} from "@/Context/SurveySessionContext";
import {postUploadedFileDescription} from "@/services/SurveyService";
import axios, {AxiosResponse} from "axios";
import Modal from "@/Components/Modal";

type ManagingFiles = {
    indicator?: Indicator | null
    file?: UploadedFile | null
}

export type FileActivity = 'reading' | 'updating' | 'uploading' | 'deleting' | undefined

type FileThumbnail = {
    id: string;
    thumbnail: Blob | null;
}

type UploadedFilesContextProps = {
    managingFiles: ManagingFiles | undefined
    setManagingFiles: Dispatch<ManagingFiles | undefined>
    sessionFiles: UploadedFile[]
    setSessionFiles: Dispatch<UploadedFile[]>
    busy: FileActivity
    globalUppy: Uppy | null,
    uppyFiles: Record<string, UppyFile<Meta, Record<string, never>>>
    readyToUpload: boolean
    loadSessionFiles: () => Promise<void>
    uploadFiles: () => Promise<void>
    uploadFilesWithContext: (indicator?: Indicator, nodeIndex?: number) => Promise<void>
    deleteFile: (file: UploadedFile) => Promise<void>
    fileDeletionCandidate: UploadedFile | null
    setFileDeletionCandidate: Dispatch<UploadedFile | null>
    updateFileDescription: (file: UploadedFile, description: string) => Promise<void>
    associateResponseFile: (session: SurveySession, file: UploadedFile, indicator: Indicator, nodeIndex: number, options: {reference?: string, comments?: string}) => Promise<IndicatorResponse>
    disassociateResponseFile: (fileId: number, responseId: number) => Promise<IndicatorResponse>
    updateFileResponseInfo: (responseUploadId: number, info: {reference?: string, comments?: string}) => Promise<void>
}


const UploadedFilesContext = createContext<UploadedFilesContextProps | undefined>(undefined)

export const UploadedFilesContextProvider = (
    {children, auth, variant, config, indicator, nodeIndex}: PropsWithChildren<SurveySessionInertiaProps & PageProps & {indicator?: Indicator | null, nodeIndex?: number}>
) => {
    const [managingFiles, setManagingFiles] = useState<ManagingFiles | undefined>(undefined);
    const [sessionFiles, setSessionFiles] = useState<UploadedFile[]>([])
    const [busy, setBusy] = useState<FileActivity>(undefined)
    const [uppyFiles, setUppyFiles] = useState<Record<string, UppyFile<Meta, Record<string, never>>>>({})
    const [readyToUpload, setReadyToUpload] = useState(false)
    const [fileDeletionCandidate, setFileDeletionCandidate] = useState<UploadedFile | null>(null)
    const thumbnailsRef = useRef<FileThumbnail[]>([])
    const awaitingThumbnails = useRef<string[]>([])

    const { session } = useSurveySessionContext()

    const updateUppyFiles = useCallback(() => {
        if (!globalUppyRef.current) return
        const files = globalUppyRef.current.getFiles();
        const filesObj = files.reduce((acc, file) => {
            acc[file.id] = file;
            return acc;
        }, {} as Record<string, UppyFile<Meta, Record<string, never>>>);
        setUppyFiles(filesObj)
    }, [])

    const setAwaitingThumbnail = (file: UppyFile<Meta, Record<string, never>>, waiting: boolean) => {
        const i = awaitingThumbnails.current.indexOf(file.id);
        if (waiting) {
            if (i === -1) {
                awaitingThumbnails.current.push(file.id);
                setReadyToUpload(false);
            }
        } else {
            if (i !== -1) awaitingThumbnails.current.splice(i, 1);
            if (awaitingThumbnails.current.length === 0) setReadyToUpload(true);
        }
    };

    const getThumbnail = (file: UppyFile<Meta, Record<string, never>>) =>
        thumbnailsRef.current.find(t => t.id === file.id)

    const setThumbnail = (thumbnail: FileThumbnail) => {
        const i = thumbnailsRef.current.findIndex(t => t.id === thumbnail.id)
        if (i === -1) thumbnailsRef.current = [...thumbnailsRef.current, thumbnail]
        else {
            const updated = [...thumbnailsRef.current]
            updated[i] = thumbnail
            thumbnailsRef.current = updated
        }
    }

    // We keep a single global uppy instance inside the context
    const globalUppyRef = useRef<Uppy<Meta, Record<string, never>> | null>(null)

    // Initialize/Reconfigure Uppy when session or config changes
    useEffect(() => {
        if (!session?.id) return;

        // Dispose previous
        if (globalUppyRef.current) {
            try {
                globalUppyRef.current.clear();
                globalUppyRef.current.cancelAll();
            } catch {}
        }

        const mimes = config.survey.upload.mimes || '*';
        const maxFileSize = config.survey.upload.max_file_size || 10485760;
        const maxCount = config.survey.upload.max_count || 10;

        const uppy = new Uppy({
            id: `GLOBAL-${session.id}`,
            autoProceed: false,
            restrictions: {
                maxFileSize: maxFileSize,
                maxNumberOfFiles: maxCount,
                allowedFileTypes: mimes ? mimes.split(',').map((m: string) => m.trim()) : undefined
            },
            onBeforeFileAdded: (currentFile) => {
                if (!config.survey.upload.allow_duplicates) {
                    const existing = sessionFiles.find(f => f.upload_filename === currentFile.name);
                    if (existing) {
                        toast.warning(`File "${currentFile.name}" already exists. Duplicates are not allowed.`);
                        return false;
                    }
                }
                if (currentFile.type?.toString().startsWith('image/')) setAwaitingThumbnail(currentFile as any, true)
                return true;
            },
            onBeforeUpload: () => {
                return false; // prevent auto upload, UI triggers
            }
        })

        uppy.use(ThumbnailGenerator, {})

        uppy.on('file-added', () => {
            updateUppyFiles();
            if (!awaitingThumbnails.current.length) setReadyToUpload(true)
        })
        uppy.on('file-removed', () => {
            updateUppyFiles();
        })
        uppy.on('thumbnail:generated', async (file, preview) => {
            try {
                const response = await fetch(preview);
                const blob = await response.blob();
                const t = getThumbnail(file) ?? { id: file.id, thumbnail: null };
                t.thumbnail = blob;
                setThumbnail(t);
                setAwaitingThumbnail(file, false);
            } catch (e) {
                console.error('Error generating thumbnail:', e);
                setAwaitingThumbnail(file, false);
            }
        })
        uppy.on('restriction-failed', (_file, error) => {
            toast.error(`Upload restriction: ${error.message}`)
        })

        globalUppyRef.current = uppy
        setUppyFiles({})
        setReadyToUpload(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session?.id, config.survey.upload.max_count, config.survey.upload.max_file_size, config.survey.upload.mimes, config.survey.upload.allow_duplicates])

    const loadSessionFiles = useCallback(async () => {
        if (!session?.id) return;
        setBusy('reading')
        try {
            const response = await fetch(route('api.session.files', { session: session.id }));
            if (response.ok) {
                const files: UploadedFile[] = await response.json();
                setSessionFiles(files)
            }
        } catch (error) {
            console.error('Error loading session files:', error);
            toast.error('Failed to load session files');
        } finally {
            setBusy(undefined)
        }
    }, [session?.id])

    const updateFileDescription = useCallback(async (file: UploadedFile, description: string) => {
        file.description = description
        postUploadedFileDescription(file, description).then(
            updatedFile => {
                updatedFile.description = description
                setSessionFiles(prev => prev.map(f => f.id === file.id ? updatedFile : f))
                toast.success('File description updated');
            }
        ).catch(error => {
            console.error('Update error:', error);
            toast.error('Failed to update file description');
        })
    }, [session?.id])

    const deleteFile = useCallback(async (file: UploadedFile) => {
        axios.delete(route('api.session.delete-upload', {
            file: file.id
        })).then(response => {
            setSessionFiles(prev => prev.filter(f => f.id !== file.id))
            toast.success(`${file.upload_filename} was deleted.`)
        }).catch(error => {
            console.error('Delete error:', error);
            toast.error('Failed to delete file description');
        }).finally(() => {
            setFileDeletionCandidate(null)
        })
    }, [])

    const associateResponseFile = useCallback(async (session: SurveySession, file: UploadedFile, indicator: Indicator, nodeIndex: number, options: {reference?: string, comments?: string}) => {
        setBusy('updating');
        return await axios
            .post(route("api.session.associate-file"), {
                ...options,
                survey_session_id: session.id,
                indicator_id: indicator.id,
                node_index: nodeIndex,
                uploaded_file_id: file.id,
            })
            .then((response: AxiosResponse<IndicatorResponse>) => {
                const updatedResponse = response.data;

                setSessionFiles(prev => prev.map(sessionFile => {
                    if (sessionFile.id === file.id) {
                        // Get existing responses that are not this indicator response
                        const existingResponses = sessionFile.responses || [];
                        const otherResponses = existingResponses.filter(resp =>
                            resp.id !== updatedResponse.id &&
                            !(resp.indicator_id === indicator.id && (resp.node_index ?? 0) === (nodeIndex ?? 0))
                        );

                        // The backend returns the response with uploadedFiles that includes the pivot data
                        // We need to find the pivot information for this specific file
                        const fileUpload = updatedResponse.uploadedFiles?.find(uf => uf.id === file.id);
                        const pivotData = fileUpload?.pivot;

                        console.log('Association response data:', {
                            responseId: updatedResponse.id,
                            fileId: file.id,
                            uploadedFiles: updatedResponse.uploadedFiles,
                            foundPivot: pivotData
                        });

                        // Create the response object for this file's responses array
                        const responseForFile = {
                            ...updatedResponse,
                            pivot: pivotData ? {
                                id: pivotData.id,
                                user_id: pivotData.user_id,
                                reference: pivotData.reference,
                                comments: pivotData.comments
                            } : {
                                // If for some reason we don't have pivot data, create a fallback
                                user_id: updatedResponse.user_id,
                                reference: options.reference || null,
                                comments: options.comments || null
                            }
                        };

                        const allResponses = [...otherResponses, responseForFile];

                        return {
                            ...sessionFile,
                            responses: allResponses
                        };
                    }
                    return sessionFile
                }));

                return updatedResponse
            })
            .catch((error: Error) => {
                console.warn(`Association failed: ${error}`)
                throw error
            })
            .finally(() => {
                setBusy(undefined);
            })
    }, [])

    const disassociateResponseFile = useCallback(async(fileId: number, responseId: number) => {
        setBusy('updating')
        return await axios
            .delete(route("api.session.disassociate-file"), {
                data: {
                    uploaded_file_id: fileId,
                    indicator_response_id: responseId
                }
            })
            .then((response: AxiosResponse<IndicatorResponse>) => {
                const updatedResponse = response.data;

                setSessionFiles(prev => prev.map(sessionFile => {
                    if (sessionFile.id === fileId && sessionFile.responses) {
                        const updatedResponses = sessionFile.responses.filter(resp =>
                            resp.id !== responseId
                        );

                        return {
                            ...sessionFile,
                            responses: updatedResponses
                        };
                    }
                    return sessionFile;
                }));

                return updatedResponse
            })
            .catch((error: Error) => {
                console.warn(`Update failed: ${error}`)
                throw error
            })
            .finally(() => {
                setBusy(undefined);
            })
    }, [])

    const updateFileResponseInfo = useCallback(async (responseUploadId: number, info: {reference?: string, comments?: string}) => {
        axios
            .patch(route("api.session.update-respond-upload-info"), {
                ...info,
                id: responseUploadId,
            })
            .then((response: AxiosResponse<UploadedFile>) => {
                return response.data
            })
            .catch((error: Error) => {
                console.warn(`Update failed: ${error}`)
                throw error
            })
            .finally(() => {
                setBusy(undefined);
            })
    }, [])

    const uploadFiles = useCallback(async () => {
        if (!globalUppyRef.current || busy || !readyToUpload || !session?.id) return;
        setBusy('uploading')
        const filesToUpload = Object.values(uppyFiles)
        if (filesToUpload.length === 0) { setBusy(undefined); return }

        try {
            const formData = new FormData();
            const info: Record<string, { name: string; meta: any }> = {};

            filesToUpload.forEach((file, index) => {
                const name = `FILE${index}`;
                formData.append("files[]", file.data as File, name);
                info[name] = { name: file.name ?? "", meta: file.meta };
                const thumbnail = getThumbnail(file);
                if (thumbnail?.thumbnail) formData.append(`thumbnails[]`, thumbnail.thumbnail, name);
            })

            formData.append('info', JSON.stringify(info));
            formData.append('survey_session_id', `${session.id}`);

            const response = await axios.post(route('api.session.upload'), formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                }
            });

            globalUppyRef.current.clear();
            updateUppyFiles();

            if (response.data.uploaded) {
                setSessionFiles(prev => [...prev, ...response.data.uploaded])
                toast.success(`${filesToUpload.length} file(s) uploaded successfully`);
            }
        } catch (error) {
            console.error('Upload error:', error);
            toast.error(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setBusy(undefined);
        }
    }, [busy, readyToUpload, session?.id, uppyFiles, updateUppyFiles])

    const uploadFilesWithContext = useCallback(async (indicator?: Indicator, nodeIndex?: number) => {
        if (!globalUppyRef.current || busy || !readyToUpload || !session?.id) return;
        setBusy('uploading')
        const filesToUpload = Object.values(uppyFiles)
        if (filesToUpload.length === 0) { setBusy(undefined); return }

        try {
            const formData = new FormData();
            const info: Record<string, { name: string; meta: any }> = {};

            filesToUpload.forEach((file, index) => {
                const name = `FILE${index}`;
                formData.append("files[]", file.data as File, name);
                info[name] = { name: file.name ?? "", meta: file.meta };
                const thumbnail = getThumbnail(file);
                if (thumbnail?.thumbnail) formData.append(`thumbnails[]`, thumbnail.thumbnail, name);
            })

            formData.append('info', JSON.stringify(info));
            formData.append('survey_session_id', `${session.id}`);

            // Add indicator context if provided
            if (indicator) {
                formData.append('indicator_id', `${indicator.id}`);
                formData.append('node_index', `${nodeIndex ?? 0}`);
            }

            console.log('Uploading with context:', {
                sessionId: session.id,
                indicatorId: indicator?.id,
                nodeIndex: nodeIndex,
                filesCount: filesToUpload.length
            });

            const response = await axios.post(route('api.session.upload'), formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                }
            });

            console.log('Upload response:', response.data);

            globalUppyRef.current.clear();
            updateUppyFiles();

            // The backend returns { uploads: [...], results: { ... } }
            const uploadedFiles = response.data.uploads || [];

            if (uploadedFiles.length > 0) {
                console.log('Adding uploaded files to sessionFiles:', uploadedFiles);

                // Update session files with the new uploads
                setSessionFiles(prev => {
                    console.log('Previous session files:', prev.length);
                    const newFiles = [...prev, ...uploadedFiles];
                    console.log('New session files:', newFiles.length);
                    return newFiles;
                });

                toast.success(`${filesToUpload.length} file(s) uploaded successfully`);

                // If we have indicator response data from the backend, update the indicator
                if (indicator && response.data.results?.indicatorResponse) {
                    console.log('Updating indicator response:', response.data.results.indicatorResponse);
                    indicator.response = response.data.results.indicatorResponse;
                }
            } else {
                console.warn('No uploaded files in response');
            }
        } catch (error) {
            console.error('Upload error:', error);
            toast.error(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        } finally {
            setBusy(undefined);
        }
    }, [busy, readyToUpload, session?.id, uppyFiles, updateUppyFiles])

    // Auto-load session files when session changes
    useEffect(() => {
        if (session?.id) {
            loadSessionFiles();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session?.id])

    return <UploadedFilesContext.Provider value={{
        globalUppy: globalUppyRef.current,
        managingFiles,
        setManagingFiles,
        sessionFiles,
        setSessionFiles,
        busy: busy,
        uppyFiles,
        readyToUpload,
        loadSessionFiles,
        uploadFiles,
        uploadFilesWithContext,
        fileDeletionCandidate,
        setFileDeletionCandidate,
        deleteFile,
        updateFileDescription,
        associateResponseFile,
        disassociateResponseFile,
        updateFileResponseInfo,
    }}>
        {children}
        <Modal show={!!fileDeletionCandidate} onClose={() => setFileDeletionCandidate(null)}
               maxWidth="3xl"
               title="Confirm file deletion"
               actions={[
                   { label: 'Delete', onClick: () => deleteFile(fileDeletionCandidate!), variant: 'danger'},
                   { label: 'Cancel', onClick: () => setFileDeletionCandidate(null), variant: 'secondary'}
               ]}
        >
            <p className="text-primary dark:text-primary-dark">Are you sure you want to delete <strong>{fileDeletionCandidate?.upload_filename}</strong>?</p>
            { fileDeletionCandidate?.responses?.length ? (
                <p className="pl-3 pt-3 text-highlight dark:text-highlight-dark">This file is currently associated with { fileDeletionCandidate.responses.length > 1 ? `${fileDeletionCandidate.responses.length} responses` : 'a response'}.</p>
            ) : null}
            <p className="pt-3 text-primary dark:text-primary-dark">
                The file will be removed from the survey session and cannot be recovered
            </p>
        </Modal>
    </UploadedFilesContext.Provider>;
}

export function useUploadedFilesContext(): UploadedFilesContextProps {
    const context = useContext(UploadedFilesContext);
    if (!context) throw new Error("No UploadedFilesContext is defined")
    return context
}

